import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-bg-subtle', className)} />;
}

const statTones = {
  default: { icon: 'bg-brand-subtle text-brand', card: '' },
  success: { icon: 'bg-success-subtle text-success', card: '' },
  warning: { icon: 'bg-warning-subtle text-warning', card: '' },
  danger: { icon: 'bg-danger-subtle text-danger', card: 'border-danger/25 bg-danger-subtle/40' },
};

export function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'default',
  hint,
  loading,
  to,
  search,
}: {
  label: string;
  value: string | number;
  icon?: React.ComponentType<{ className?: string }>;
  tone?: keyof typeof statTones;
  hint?: string;
  loading?: boolean;
  to?: string;
  search?: Record<string, unknown>;
}) {
  const showDanger = tone === 'danger' && Number(value) > 0;
  const t = statTones[tone];
  const inner = (
    <>
      {loading ? (
        <>
          <Skeleton className="mb-3 h-9 w-9 rounded-lg" />
          <Skeleton className="mb-2 h-8 w-16" />
          <Skeleton className="h-3.5 w-24" />
        </>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between">
            {Icon && (
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-lg',
                  showDanger ? 'bg-danger-subtle text-danger' : t.icon,
                )}
              >
                <Icon className="h-[18px] w-[18px]" />
              </div>
            )}
          </div>
          <p className="text-[30px] font-semibold leading-none tracking-tight">{value}</p>
          <p className="mt-2 text-[13px] font-medium text-text-secondary">{label}</p>
          {hint && <p className="mt-0.5 text-xs text-text-muted">{hint}</p>}
        </>
      )}
    </>
  );

  const className = cn(
    'group rounded-xl border border-border bg-bg-surface p-5 shadow-card transition-all duration-200',
    to && !loading && 'cursor-pointer hover:border-brand/30 hover:shadow-raised',
    !to && 'hover:shadow-raised',
    showDanger && t.card,
  );

  if (to && !loading) {
    return (
      <Link to={to} search={search as never} className={className}>
        {inner}
      </Link>
    );
  }

  return <div className={className}>{inner}</div>;
}

export function Card({
  title,
  description,
  action,
  children,
  className,
  bodyClassName,
  noPadding,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  noPadding?: boolean;
}) {
  return (
    <div className={cn('rounded-xl border border-border bg-bg-surface shadow-card', className)}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
          <div>
            {title && <h2 className="text-[15px] font-semibold leading-tight">{title}</h2>}
            {description && <p className="mt-0.5 text-[13px] text-text-secondary">{description}</p>}
          </div>
          {action}
        </div>
      )}
      <div className={cn(!noPadding && 'p-5', bodyClassName)}>{children}</div>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-bg-subtle ring-1 ring-border">
        <Icon className="h-7 w-7 text-text-muted" />
      </div>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-1.5 max-w-sm text-sm text-text-secondary">{description}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function UsageMeter({
  label,
  used,
  limit,
  percent,
}: {
  label: string;
  used: number;
  limit: number;
  percent: number;
}) {
  const warning = percent >= 70;
  const critical = percent >= 90;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-text-secondary">{label}</span>
        <span className={cn('font-semibold tabular-nums', critical ? 'text-danger' : warning ? 'text-warning' : 'text-text-primary')}>
          {used} <span className="font-normal text-text-muted">/ {limit}</span>
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-bg-subtle">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            critical ? 'bg-danger' : warning ? 'bg-warning' : 'bg-brand',
          )}
          style={{ width: `${Math.min(Math.max(percent, 2), 100)}%` }}
        />
      </div>
      {warning && (
        <p className={cn('text-xs', critical ? 'text-danger' : 'text-warning')}>
          {critical ? "You've nearly reached your plan limit." : "You're approaching your plan limit."}
        </p>
      )}
    </div>
  );
}
